/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/* eslint-disable jsx-a11y/alt-text */
import { unwrapResult } from '@reduxjs/toolkit'
import * as H from 'history'
import { FC, ReactElement, useCallback, useEffect, useState } from 'react'
import { TFunction } from 'react-i18next'
import { Link } from 'react-router-dom'

import { ACCESS_GUARANTEE_PRODUCT_TYPE, ADD_ON_PRODUCT_TYPE, OFFERING_PRODUCT_TYPE, topicIdMap } from '../../../constants'
import { deleteItemFromCart, updateItemInCart, updateVariantInCart } from '../../../features/cart/cartSlice'
import { useAppDispatch, useAppSelector } from '../../../store'
import { CartItem } from '../../../types'
import {
  getAddOnsDependenciesFromCart,
  getAccessGuaranteePeriod,
  getAmountWithCurrency,
  isAccessGuarantee,
  isAccessGuaranteeApplicableToItem,
} from '../../../utils'
import { PdpBreadCrumbsProp } from '../../breadcrumbs/pdpBreadCrumbs/pdpBreadCrumbs'
import HelpCategoryLink from '../../help/helpCategoryLink'
import { openSnackbar } from '../../snackbar'
import CartItemImage from '../cartItemImage'
import OptionalDependencies from './optionalDependencies'

import './index.css'

export type CartItemProps = {
  t: TFunction<'translation', undefined>
  cartItem: CartItem
  history?: H.History<PdpBreadCrumbsProp>
  page?: boolean
  errors?: string[]
}

const HandleOutput: FC<{ page: boolean }> = ({ page, children }) => {
  return page ? <div className="cart-item-card-container">{children}</div> : <>{children}</>
}

const CartItemCard: FC<CartItemProps> = ({ t, cartItem, history, page, errors }): ReactElement => {
  const dispatch = useAppDispatch()
  const { isAccessGuaranteeAppliedToCart, cartItems } = useAppSelector((state) => state.cartReducer)
  const { settings } = useAppSelector((state) => state.appConfigReducer)

  const [quantity, setQuantity] = useState(cartItem.quantity)
  const [pricing, setPricing] = useState<number>(0)
  const [totalMsrp, setTotalMrsp] = useState<number>(0)

  const [cardErrors, setCardErrors] = useState<string[]>([])
  const allErrors = errors ? [...errors, ...cardErrors] : cardErrors

  const getAGText = useCallback(() => {
    return `${
      cartItem.cartItemApiInfo.accessGuaranteeStatus === 'Extended' || isAccessGuaranteeAppliedToCart ? 'Extended' : ''
    } Access Guarantee (${getAccessGuaranteePeriod(isAccessGuaranteeAppliedToCart)})
    ${isAccessGuaranteeAppliedToCart ? 'Applied' : ''}`
  }, [cartItem, isAccessGuaranteeAppliedToCart])

  const updateItem = async (newCartItem: CartItem) => {
    return await dispatch(updateItemInCart(newCartItem))
      .then(unwrapResult)
      .then(
        () => {
          setCardErrors([])
          return true
        },
        (reason: string) => {
          if (!cardErrors.includes(reason)) {
            setCardErrors((cardErrors) => cardErrors.concat(reason))
          }
          return false
        }
      )
  }

  const updateVariant = async (newCartItem: CartItem) => {
    return await dispatch(updateVariantInCart({ oldCartItem: cartItem, newCartItem }))
      .then(unwrapResult)
      .then(
        () => true,
        (reason: string) => {
          openSnackbar(reason, 6, 'topRight', 'failure')
          return false
        }
      )
  }
  const removeItem = async () => {
    return await dispatch(deleteItemFromCart(cartItem))
      .then(unwrapResult)
      .then(
        () => true,
        (reason: string) => {
          if (!cardErrors.includes(reason)) {
            setCardErrors((cardErrors) => cardErrors.concat(reason))
          }
          return false
        }
      )
  }
  const handleQuantity = async (base: number) => {
    if (quantity + base > 0) {
      const newCartItem: CartItem = {
        ...cartItem,
        quantity: quantity + base,
        cartItemApiInfo: { ...cartItem.cartItemApiInfo, quantity: quantity + base, priceInCart: pricing * (quantity + base) },
      }
      if (await updateItem(newCartItem)) setQuantity(quantity + base)
    } else await removeItem()
  }

  const handleVariantChange = async (variantId: string) => {
    const newVariant = cartItem.product?.variants.find((v) => v.productVariantId === variantId)
    if (newVariant) {
      const { productVariantId, description, productVariantType, pricing } = newVariant
      const newCartItem: CartItem = {
        ...cartItem,
        variant: newVariant,
        cartItemApiInfo: {
          ...cartItem.cartItemApiInfo,
          productVariantId,
          productVariantType,
          priceInCart: pricing.price,
          productVariantName: description.short as string,
        },
      }
      await updateVariant(newCartItem)
    }
  }

  useEffect(() => {
    if (quantity) {
      setPricing(quantity * (cartItem.variant?.pricing.price as number))
      setTotalMrsp(quantity * (cartItem.variant?.pricing.msrp as number))
    }
  }, [cartItem.variant?.pricing.msrp, cartItem.variant?.pricing.price, quantity])

  useEffect(() => {
    if (cartItem.quantity !== quantity) {
      setQuantity(cartItem.quantity)
    }
  }, [cartItem.quantity, quantity])

  const link = useCallback(
    (content: JSX.Element | ReactElement | Element | string): ReactElement => {
      return history ? (
        <Link
          to={{
            pathname: `/products/${cartItem.product?.productId}`,
            search: `productVariant=${cartItem.variant?.productVariantId}`,
            state: {
              path: history.location.pathname,
              search: history.location.search,
              productName: cartItem.product?.name,
              locationPath: `/products/${cartItem.product?.productId}/?productVariant=${cartItem.variant?.productVariantId}`,
            },
          }}
          title={cartItem.product?.name || 'Product Description Card'}
          style={{ textDecoration: 'none' }}
          aria-label={t('Common_viewProductDetails') + ': ' + cartItem.product?.name}
        >
          {content}
        </Link>
      ) : (
        <a
          href={`/products/${cartItem.product?.productId}/?productVariant=${cartItem.variant?.productVariantId}`}
          title={cartItem.product?.name || 'Product Description Card'}
          style={{ textDecoration: 'none' }}
          aria-label={t('Common_viewProductDetails') + ': ' + cartItem.product?.name}
        >
          {content}
        </a>
      )
    },
    [cartItem.product?.name, cartItem.product?.productId, history, cartItem.variant?.productVariantId, t]
  )
  return (
    <HandleOutput page={!!page}>
      <div className={`cart-item-wrapper ${!page ? 'with-underline' : ''}`}>
        <div className="cart-item-left-container">
          {cartItem.variant && isAccessGuarantee(cartItem.variant.productVariantType) ? (
            <CartItemImage cartItem={cartItem} />
          ) : (
            link(<CartItemImage cartItem={cartItem} />)
          )}
        </div>
        <div className="cart-item-middle-container">
          <div className="cart-item-middle-info-container">
            <div className="cart-item-middle-info-order-container">
              <div className="cart-item-middle-info-order-title">
                {cartItem.cartItemApiInfo.productVariantType.endsWith(ACCESS_GUARANTEE_PRODUCT_TYPE) ? (
                  <p>{cartItem.product?.name ?? ''}</p>
                ) : (
                  link(cartItem.product?.name ?? '')
                )}
              </div>
              {cartItem.product?.variants.length ? (
                <div className="cart-item-middle-info-order-subs-options">
                  <select
                    value={cartItem.variant?.productVariantId}
                    className="cart-item-middle-info-order-subs-select"
                    name="cart-item-subscription-option"
                    onChange={(e) => handleVariantChange(e.target.value)}
                    aria-label={cartItem.product?.name + t('Common_productVarations')}
                  >
                    {cartItem.product.variants
                      .filter(
                        (v) =>
                          v.productVariantType === OFFERING_PRODUCT_TYPE ||
                          v.productVariantType === ACCESS_GUARANTEE_PRODUCT_TYPE ||
                          (v.productVariantType === ADD_ON_PRODUCT_TYPE &&
                            getAddOnsDependenciesFromCart(cartItems, cartItem).includes(v.productVariantId))
                      )
                      .map((v, index) => (
                        <option key={index} value={v.productVariantId}>
                          {v.description.short}
                        </option>
                      ))}
                  </select>
                </div>
              ) : null}
              <div className="cart-item-middle-info-order-access-guarantee-container">
                {quantity ? (
                  <div className="cart-item-middle-info-order-amount-container">
                    <div className="cart-item-middle-info-order-amount-minus">
                      <button
                        className="cart-item-middle-info-order-amount-minus-btn"
                        onClick={async () => await handleQuantity(-1)}
                        aria-label={t('Common_decreaseQuantity')}
                      >
                        -
                      </button>
                    </div>
                    <div className="cart-item-middle-info-order-amount">{quantity}</div>
                    <div className="cart-item-middle-info-order-amount-plus">
                      <button
                        className="cart-item-middle-info-order-amount-plus-btn"
                        onClick={async () => await handleQuantity(+1)}
                        aria-label={t('Common_increaseQuantity')}
                      >
                        +
                      </button>
                    </div>
                  </div>
                ) : null}
                <div className="cart-item-middle-info-price-container">
                  <div
                    className="heavy cart-item-middle-info-current-price"
                    tabIndex={0}
                    aria-label={
                      t('Common_priceInCart') +
                      ': ' +
                      getAmountWithCurrency(settings.locale.language, settings.currency.currencyAbbreviation, pricing)
                    }
                  >
                    <div className="cart-item-middle-info-current-price-bg" aria-hidden>
                      {getAmountWithCurrency(settings.locale.language, settings.currency.currencyAbbreviation, pricing || 0)}
                    </div>
                  </div>
                  {totalMsrp ? (
                    <div
                      className="cart-item-middle-info-saved-price"
                      tabIndex={0}
                      aria-label={
                        t('Cart_saved') +
                        ' ' +
                        getAmountWithCurrency(
                          settings.locale.language,
                          settings.currency.currencyAbbreviation,
                          totalMsrp ? totalMsrp - pricing : 0
                        )
                      }
                    >
                      {t('Cart_saved')}{' '}
                      {getAmountWithCurrency(
                        settings.locale.language,
                        settings.currency.currencyAbbreviation,
                        totalMsrp ? totalMsrp - pricing : 0
                      )}
                    </div>
                  ) : null}
                </div>
                {isAccessGuaranteeApplicableToItem(cartItem) && (
                  <div className="cart-item-middle-guarantee-container">
                    <div className="cart-item-middle-guarantee-text">
                      {page ? (
                        <>
                          <span>{getAGText()}</span>
                          <HelpCategoryLink
                            helpCategoryId={topicIdMap.accessGuarantee}
                            ariaLabel={t('WebStore_learnMoreAboutExtendedAccessGuarantee')}
                          >
                            <button className="cart-item-middle-guarantee-learn-more-btn">{t('Common_learnMore')}</button>
                          </HelpCategoryLink>
                        </>
                      ) : (
                        <HelpCategoryLink
                          helpCategoryId={topicIdMap.accessGuarantee}
                          ariaLabel={`${t('WebStore_learnMoreAboutExtendedAccessGuarantee')}
                       ${getAccessGuaranteePeriod(isAccessGuaranteeAppliedToCart)})`}
                        >
                          <>
                            <i className="feather-shield" style={{ color: 'green' }}></i>(
                            {getAccessGuaranteePeriod(isAccessGuaranteeAppliedToCart)})
                          </>
                        </HelpCategoryLink>
                      )}
                    </div>
                  </div>
                )}
                {cartItem.cartItemApiInfo.productVariantType.endsWith(ACCESS_GUARANTEE_PRODUCT_TYPE) && (
                  <div className="cart-item-middle-guarantee-container">
                    {page ? (
                      <div className="cart-item-middle-guarantee-text">
                        {t('AccessGuarantee_extendedOfferingDescription')}
                        <HelpCategoryLink
                          className="cart-item-middle-guarantee-link"
                          helpCategoryId={topicIdMap.accessGuarantee}
                          ariaLabel={t('WebStore_learnMoreAboutExtendedAccessGuarantee')}
                        >
                          <button className="cart-item-middle-guarantee-learn-more-btn">{t('Common_learnMore')}</button>
                        </HelpCategoryLink>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="cart-item-right-container">
          <div className="cart-item-delete-btn-container">
            <button className="cart-item-delete-btn" onClick={removeItem} aria-label="Remove Item">
              <i className="feather-x-circle"></i>
            </button>
          </div>
        </div>
      </div>
      {allErrors.map((e, i) => (
        <div key={e + i} className="error-message" role="alert">
          <i className="feather-x error-message-icon" aria-label="failure icon"></i>
          <div className="error-message-content">{e}</div>
        </div>
      ))}
      {page && !cartItem.cartItemApiInfo.addOnForProductVariantId && cartItem.variant && (
        <div className="cart-item-optional-section">
          <OptionalDependencies variant={cartItem.variant} history={history} />
        </div>
      )}
    </HandleOutput>
  )
}

export default CartItemCard
